/**
 * PostHog feature-flag client for the browser frontend.
 *
 * Reads `VITE_POSTHOG_KEY` (and optional `VITE_POSTHOG_HOST`) from the
 * environment. When the key is absent this returns a no-op client so nothing in
 * the app fails before PostHog credentials exist — every flag falls back to its
 * supplied default.
 */

import posthog from "posthog-js";

export interface FlagsClient {
  readonly enabled: boolean;
  isFeatureEnabled: (key: string, defaultValue?: boolean) => boolean;
  getFeatureFlag: (key: string, defaultValue?: unknown) => unknown;
  getAllFlags: () => Record<string, unknown>;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  /**
   * Subscribe to flag-resolution changes (PostHog loads flags async).
   * Returns an unsubscribe function. The no-op client never fires.
   */
  onFeatureFlags: (cb: () => void) => () => void;
}

export interface PostHogFlagsOptions {
  key?: string;
  host?: string;
}

const noopClient: FlagsClient = {
  enabled: false,
  isFeatureEnabled: (_key, defaultValue = false) => defaultValue,
  getFeatureFlag: (_key, defaultValue) => defaultValue,
  getAllFlags: () => ({}),
  identify: () => undefined,
  capture: () => undefined,
  onFeatureFlags: () => () => undefined,
};

/** Create a PostHog-backed flags client, or a safe no-op when unconfigured. */
export function createFlagsClient(options: PostHogFlagsOptions = {}): FlagsClient {
  const key = options.key ?? import.meta.env?.VITE_POSTHOG_KEY;
  const host = options.host ?? import.meta.env?.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

  if (!key) {
    return noopClient;
  }

  // `posthog.init` is a global side effect: guard on `__loaded` so repeated
  // calls (HMR, a second client instance, tests) never double-initialize.
  const loaded = posthog as typeof posthog & { __loaded?: boolean };
  if (!loaded.__loaded) {
    posthog.init(key, {
      api_host: host,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
    });
  }

  return {
    enabled: true,
    isFeatureEnabled: (flag, defaultValue = false) =>
      posthog.isFeatureEnabled(flag, { send_event: false }) ?? defaultValue,
    getFeatureFlag: (flag, defaultValue) =>
      posthog.getFeatureFlag(flag, { send_event: false }) ?? defaultValue,
    getAllFlags: () => (posthog as unknown as { getAllFlags?: () => Record<string, unknown> }).getAllFlags?.() ?? {},
    identify: (id, props) => posthog.identify(id, props),
    capture: (event, props) => posthog.capture(event, props),
    onFeatureFlags: (cb) => {
      // `posthog.onFeatureFlags` fires when flags load/change and returns an
      // unsubscribe function. Typed loosely to avoid coupling to posthog-js
      // internals; fall back to a no-op unsubscribe if unavailable.
      const off = (
        posthog as unknown as {
          onFeatureFlags?: (cb: () => void) => () => void;
        }
      ).onFeatureFlags?.(cb);
      return off ?? (() => undefined);
    },
  };
}

export { noopClient };
